import { ref } from "vue";
import { apiService } from "@/services/apiService";
import { useDeviceVerifierStore } from "@/stores/deviceVerifier";
import { useSystemSettingsStore } from "@/stores/systemSettings";
import { useCds } from "@/composables/useCds";

export function usePos() {
  const loading = ref(false);
  const error = ref(null);

  const fetchPosById = async (id) => {
    loading.value = true;
    try {
      return await apiService.get(`/kiosks/${id}`);
    } catch (err) {
      error.value = err?.message || "Error fetching POS";
      return null;
    } finally {
      loading.value = false;
    }
  };

  const fetchPosClientId = async () => {
    try {
      const { getPosClientId, setPosClientId } = useSystemSettingsStore();
      const { saveCdsClientId } = useCds();
      let clientId = getPosClientId();
      if (!clientId) {
        const pos = await getPosByXClientId();
        if (pos && pos.type === "POS" && pos.status && pos.uuid) {
          /* Save CDS client id for websocket */
          await saveCdsClientId(pos);

          /* Save POS client id for websocket */
          setPosClientId(pos.uuid);
          clientId = pos.uuid;
        }
      }
      return clientId;
    } catch (err) {
      error.value =
        err?.message || `Error performing fetch operation`;
      throw err;
    }
  }

  const handlePosAssociation = async (id, operation, posData) => {
    loading.value = true;
    try {
      let posOtp;
      let endpoint;
      switch (operation) {
        case "verifyOtp":
          endpoint = `/kiosks/verify-otp`;
          posOtp = await apiService.post(endpoint, posData);
          break;

        default:
          break;
      }

      /** 
       * To determine whether the POS is associated or not.
       * No other data in response to determine the result.
       */
      const detail = posOtp?.detail || '';
      const status = detail.trim().toLowerCase() === 'kiosk verified and uuid associated';
      return {...posOtp, status: status};
    } catch (err) {
      error.value =
        err?.message || `Error performing patch operation: ${operation}`;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const getPosByXClientId = async () => {
    try {
      const { getUuid } = useDeviceVerifierStore();
      if (getUuid().length > 0) {
        const response = await apiService.get('/kiosks/get_kiosk');
        if (response.id) {
          return response;
        }
      }
      return null;
    } catch (err) {
      error.value =
        err?.detail ||
        err?.message ||
        'Error fetching kiosk';
      throw err;
    }
  };

  return {
    fetchPosById,
    fetchPosClientId,
    getPosByXClientId,
    handlePosAssociation,
  }
}